/**
 * StartBoostTournamentBanner — iter87.199.161. Persistent (non-dismissable
 * — this isn't a nudge, picks literally can't open without it) call-out
 * for a Weekly Spread (NFL/EPL/La Liga Boost) league created mid-season, where
 * the admin deliberately holds off opening picks until everyone invited
 * has had a fair chance to join. See `matchday_boost.py::pending_start`.
 *
 * Renders for both admins (with the "Start Tournament: Week X" button)
 * and everyone else (read-only "waiting on your admin" note) — hidden
 * entirely once the league has started.
 */
import { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function StartBoostTournamentBanner({ tournament, me, boostState, onStarted }) {
    const [starting, setStarting] = useState(false);
    if (!tournament?.id || !boostState?.pending_start) return null;

    const isAdmin = !!(me?.is_admin || me?.is_co_admin);
    const week = boostState.start_week_candidate;
    const spreadLabel = {
        epl_boost_api: "Premier League",
        la_liga_boost_api: "La Liga",
    }[tournament.template] || "NFL";

    const start = async () => {
        setStarting(true);
        try {
            const r = await api.startBoostTournament(tournament.id);
            toast.success(`Tournament started — Week ${r.start_week} is live ⚡`);
            onStarted?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start the tournament");
        } finally {
            setStarting(false);
        }
    };

    return (
        <div
            className="brutal-card-light p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-volt/40"
            data-testid="start-boost-tournament-banner"
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Flag className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm">This {spreadLabel} Weekly Spread league hasn't started yet</p>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        {isAdmin
                            ? `Invite your crew, then start it whenever you're ready — picks open from Week ${week ?? "—"} onward.`
                            : `Waiting on your admin to start the league — picks will open from Week ${week ?? "—"}.`}
                    </p>
                </div>
            </div>
            {isAdmin && (
                <button
                    type="button"
                    onClick={start}
                    disabled={starting}
                    data-testid="start-boost-tournament-btn"
                    className="brutal-btn text-xs py-2 bg-volt text-black border-black shadow-brutal disabled:opacity-50 shrink-0 self-center sm:self-auto"
                >
                    <Flag className="w-3.5 h-3.5" /> {starting ? "Starting…" : `Start Tournament: Week ${week ?? "—"}`}
                </button>
            )}
        </div>
    );
}
